import { styled } from '@linaria/react';
import { component, fonts, primitives, semantic, size, space, textStyle } from '../../theme/tokens';

export const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space['space-100']};
`;

export const StyledButton = styled.button<{
  $fullWidth: boolean;
}>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
  font-family: ${textStyle.body.lg.strong.fontFamily};
  font-weight: ${textStyle.body.lg.strong.fontWeight};
  letter-spacing: ${textStyle.body.lg.strong.letterSpacing};
  line-height: ${textStyle.body.lg.strong.lineHeight};
  border-radius: ${size.radius['radius-full']};
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  height: ${primitives.size['size-1200']};
  padding: ${space['space-300']} ${space['space-500']};
  font-size: ${textStyle.body.lg.strong.fontSize};

  &[data-size='small'] {
    height: ${primitives.size['size-1000']};
    padding: ${space['space-100']} ${space['space-400']};
    font-size: ${textStyle.body.md.strong.fontSize};
  }

  &[data-size='large'] {
    height: ${primitives.size['size-1400']};
    padding: ${space['space-400']} ${space['space-600']};
    font-size: ${textStyle.body.lg.strong.fontSize};
  }

  background-color: ${component.button.primary.enabled.bg};
  color: ${component.button.primary.enabled.text};
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }

  &[data-variant='primary'] {
    &:disabled {
      background-color: ${component.button.primary.disabled.bg};
      color: ${component.button.primary.disabled.text};
      opacity: 1;
    }

    &:focus-visible {
      background-color: ${component.button.primary.focussed.bg};
      color: ${component.button.primary.focussed.text};
    }

    &:hover:not(:disabled) {
      background-color: ${component.button.primary.hover.bg};
      color: ${component.button.primary.hover.text};
    }

    &:active:not(:disabled) {
      background-color: ${component.button.primary.pressed.bg};
      color: ${component.button.primary.pressed.text};
    }
  }

  &:not([data-variant='primary']):hover:not(:disabled) {
    opacity: 0.85;
  }

  &[data-variant='secondary'] {
    background-color: ${semantic.bg.secondary};
    color: ${semantic.text.primary};

    &:focus-visible {
      background-color: ${semantic.bg.surfaceDefault};
      color: ${semantic.text.primary};
    }

    &:disabled {
      background-color: ${semantic.bg.surfaceSubtle};
      color: ${semantic.text.disabled};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background-color: ${semantic.bg.primary};
      color: ${semantic.text.inverse};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background-color: ${semantic.bg.primary};
      color: ${semantic.text.inverse};
    }
  }

  &[data-variant='outline'] {
    background-color: ${semantic.bg.surfaceSubtle};
    color: ${semantic.text.primary};

    &:focus-visible {
      background-color: ${semantic.bg.surfaceDefault};
      color: ${semantic.text.primary};
    }

    &:disabled {
      background-color: ${semantic.bg.surfaceSubtle};
      color: ${semantic.text.disabled};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background-color: ${semantic.bg.surfaceDefault};
      color: ${semantic.text.primary};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background-color: ${semantic.bg.primary};
      color: ${semantic.text.inverse};
    }
  }

  &[data-variant='toggle'] {
    background-color: transparent;
    color: ${semantic.text.primary};
    border: ${size.stroke['stroke-default']} solid ${semantic.border.default};
    border-radius: ${size.radius['radius-sm']};
    font-weight: ${fonts.weight.medium};

    &:hover:not(:disabled) {
      background-color: ${semantic.bg.surfaceSubtle};
      border-color: ${semantic.border.default};
      color: ${semantic.text.primary};
    }

    &[data-selected] {
      background-color: ${semantic.bg.brand};
      border-color: ${semantic.border.default};
      color: ${semantic.text.inverse};

      &:hover:not(:disabled) {
        background-color: ${semantic.bg.brand};
        border-color: ${semantic.border.default};
        color: ${semantic.text.inverse};
        opacity: 0.85;
      }
    }
  }

  &[data-variant='icon'] {
    height: ${space['space-1000']};
    padding: ${space['space-200']} ${space['space-400']};
    border-radius: ${size.radius['radius-full']};
    background: ${semantic.bg.surfaceSubtle};
    color: ${semantic.text.primary};
    border: none;

    &:focus-visible {
      background: ${semantic.bg.surfaceDefault};
      color: ${semantic.text.primary};
    }

    &:disabled {
      background: ${semantic.bg.surfaceSubtle};
      color: ${semantic.text.disabled};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background: ${semantic.bg.surfaceDefault};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background: ${semantic.bg.primary};
      color: ${semantic.text.inverse};
    }
  }

  &[data-variant='danger'] {
    background-color: ${component.button.danger.enabled.bg};
    color: ${component.button.danger.enabled.text};

    &:focus-visible {
      background-color: ${component.button.danger.focussed.bg};
      color: ${component.button.danger.focussed.text};
      outline-color: ${semantic.bg.urgencyStrong};
    }

    &:disabled {
      background-color: ${component.button.danger.disabled.bg};
      color: ${component.button.danger.disabled.text};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background-color: ${component.button.danger.hover.bg};
      color: ${component.button.danger.hover.text};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background-color: ${component.button.danger.pressed.bg};
      color: ${component.button.danger.pressed.text};
    }
  }

  &[data-variant='icon-ghost'] {
    height: ${space['space-1000']};
    padding: ${space['space-100']} ${space['space-400']};
    border-radius: ${size.radius['radius-full']};
    background: transparent;
    color: ${semantic.text.primary};
    border: none;

    &:focus-visible {
      outline-offset: -2px;
    }

    &:hover:not(:disabled) {
      background: ${semantic.bg.surfaceDefault};
      opacity: 1;
    }
  }

  &[data-variant='icon-overlay'] {
    height: ${space['space-1000']};
    padding: ${space['space-100']} ${space['space-400']};
    border-radius: ${size.radius['radius-full']};
    background: ${semantic.bg.overlayBlur};
    backdrop-filter: blur(${size.blur['blur-lg']});
    color: ${semantic.text.inverse};
    border: none;

    &:focus-visible {
      background: ${semantic.bg.overlayBlurStrong};
      outline-color: ${semantic.text.inverse};
      outline-offset: -2px;
    }

    &:disabled {
      color: ${semantic.icon.disabled};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background: ${semantic.bg.overlayBlurStrong};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background: ${semantic.bg.overlayBlurStrong};
    }
  }
`;
